import { MetricDefinition } from 'libs/end-to-end/src/types/databases/metrics/metric-definition';
import React from 'react';
import { FilterInformation } from 'apps/sigma2-ui/src/lib/hooks/filter-state';
import { OnSelect } from 'apps/sigma2-ui/src/lib/hooks/useDynamicFilters';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { IconCircularArrowWithDotSvg } from 'apps/sigma2-ui/src/components/svg/IconCircularArrowWithDot';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';

const isDisabled = ({
  selectedMetric,
  isProcessing,
  disableAnalyze,
}: {
  selectedMetric: string | null;
  isProcessing: boolean | undefined;
  disableAnalyze: () => boolean;
}) => {
  return !selectedMetric || isProcessing || disableAnalyze();
};

export const BlueTopFilterAndMetricBar: React.FC<{
  primaryFilter: FilterInformation | null;
  secondaryFilter: FilterInformation | null;
  dynamicMetrics: MetricDefinition[] | null;
  selectedMetric: string | null;
  onSelectFilter: OnSelect;
  onSelectMetric: (value: string | null) => void;
  onAnalyze: () => void;
  disableAnalyze: () => boolean;
  isProcessing?: boolean;
  // eslint-disable-next-line complexity
}> = ({
  primaryFilter,
  secondaryFilter,
  dynamicMetrics,
  selectedMetric,
  onSelectFilter,
  onSelectMetric,
  onAnalyze,
  disableAnalyze,
  isProcessing,
}) => {
  const language = useLanguage();

  const shouldShowPrimaryFilter = primaryFilter !== null;
  const shouldShowSecondaryFilter = secondaryFilter !== null;
  const shouldShowMetricFilter =
    dynamicMetrics !== null && dynamicMetrics.length > 1;

  if (
    !shouldShowMetricFilter &&
    !shouldShowPrimaryFilter &&
    !shouldShowSecondaryFilter
  ) {
    return <></>;
  }

  return (
    <Spacing
      direction="horizontal"
      size="lg"
      className="w-full justify-between bg-blue-100 border-1 border-blue-500 items-center rounded-xl px-4"
    >
      <Spacing direction="horizontal" size="lg" className="items-center p-2">
        <S2Typography
          variant="headline-7-alt"
          translatedContent={language.viewBy()}
        />

        {shouldShowPrimaryFilter && !!primaryFilter && (
          <SelectBoxWithSearch
            type="multi"
            list={primaryFilter.values.map((value) => {
              return { optionLabel: value, optionValue: value };
            })}
            disabled={isProcessing}
            label={primaryFilter.displayName}
            selected={primaryFilter.selectedValues}
            setSelected={(selectedValues) => {
              if (typeof selectedValues !== 'function' && primaryFilter) {
                onSelectFilter({
                  columnHeaderValue: primaryFilter.columnHeaderValue,
                  selectedValues: selectedValues,
                });
              }
            }}
          />
        )}

        {shouldShowSecondaryFilter && !!secondaryFilter && (
          <SelectBoxWithSearch
            type="multi"
            list={secondaryFilter.values.map((value) => {
              return { optionLabel: value, optionValue: value };
            })}
            disabled={isProcessing}
            label={secondaryFilter.displayName}
            selected={secondaryFilter.selectedValues}
            setSelected={(selectedValues) => {
              if (typeof selectedValues !== 'function' && secondaryFilter) {
                onSelectFilter({
                  columnHeaderValue: secondaryFilter.columnHeaderValue,
                  selectedValues: selectedValues,
                });
              }
            }}
          />
        )}

        {shouldShowMetricFilter && !!dynamicMetrics && (
          <SelectBoxWithSearch
            type="single"
            list={dynamicMetrics.map(({ columnHeaderValue, displayName }) => {
              return {
                optionLabel: displayName,
                optionValue: columnHeaderValue,
              };
            })}
            disabled={isProcessing}
            label={getTranslatedContentAsString(
              language.metricBarSelectLabel(),
            )}
            selected={selectedMetric ?? ''}
            setSelected={(selectedValue) => {
              onSelectMetric(selectedValue);
            }}
          />
        )}

        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              if (
                !isDisabled({ selectedMetric, isProcessing, disableAnalyze })
              ) {
                onAnalyze();
              }
            },
          }}
          disabled={isDisabled({
            selectedMetric,
            isProcessing,
            disableAnalyze,
          })}
          text={'Analyze'}
        />
      </Spacing>

      <button
        onClick={() => {}}
        aria-label={getTranslatedContentAsString(language.reset())}
        title={getTranslatedContentAsString(language.reset())}
      >
        <IconCircularArrowWithDotSvg
          svg={{ className: 'w-6 h-6' }}
          wantsCurrentColor
        />
      </button>
    </Spacing>
  );
};
